import { type CountryCode, type ProjectCode } from '@constants'
import { useDebounceValueV2 } from '@dni-hooks/useDebounceValueV2'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiECommerceProducts, useApiMutationECommerceProducts } from '@hooks-api/ec/use-api-ecommerce-products'
import { useEffect, useMemo } from 'react'
import { parseProductMap } from '../parser/parseProductMap'
import { type CartItem } from '../types'
import { createDedupeProductIdsHandlers } from '../utils/createDedupeProductIdsHandlers'
const { removeDedupeProductIds, updateAndGetDedupeCartItemProductIdsSet, parseCartItemProductIdsSet } = createDedupeProductIdsHandlers()

export interface UseECommerceProductsInfoProps {
  cartItems: CartItem[]
  projectCode: ProjectCode
  countryCode: CountryCode
  extraProductIds?: CartItem['cartItemId'][]
  extraSubshipProductIds?: CartItem['cartItemId'][]
}

export const useECommerceProductsInfo = (
  props: UseECommerceProductsInfoProps,
) => {
  const { cartItems, projectCode, countryCode, extraProductIds, extraSubshipProductIds } = props
  const { trigger: getProducts } = useApiMutationECommerceProducts()
  /**
   * unmount 時清除 extraProductIds
   * Note: deps 用 stringify 判斷，防止因為是 array 的 reference 改變而執行 callback
   */
  useEffect(() => {
    return () => {
      removeDedupeProductIds(extraProductIds || [])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(extraProductIds)])

  const { productIds, subshipProductIds } = useMemo(() => {
    const { productIdsSet, subshipProductIdsSet } = updateAndGetDedupeCartItemProductIdsSet(cartItems, extraProductIds)
    const subshipProductIdsSetWithExtra = Array.isArray(extraSubshipProductIds)
      ? new Set([...subshipProductIdsSet, ...extraSubshipProductIds])
      : subshipProductIdsSet

    return {
      productIds: [...productIdsSet].join(','),
      subshipProductIds: [...subshipProductIdsSetWithExtra].join(','),
    }
  }, [cartItems, extraProductIds, extraSubshipProductIds])
  const debouncedProductIds = useDebounceValueV2(productIds, 500)

  const { destructuredData: products, mutate, isLoading: isProductsLoading, isValidating: isProductsValidating } = useApiECommerceProducts({
    project_code: projectCode,
    country_code: countryCode,
    product_ids: debouncedProductIds,
    subship_product_ids: subshipProductIds,
  }, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  })

  const productMap = useMemo(() => parseProductMap(products), [products])

  /**
   * 重新取得商品資料，並透過 mutate 更新 swr cache
   */
  const revalidateProductsInfo = useCallbackEvent(async (extraProductIds?: CartItem['cartItemId'][]) => {
    const { productIdsSet, subshipProductIdsSet } = parseCartItemProductIdsSet(cartItems, extraProductIds)
    const res = await getProducts({
      project_code: projectCode,
      country_code: countryCode,
      product_ids: [...productIdsSet].join(','),
      subship_product_ids: [...subshipProductIdsSet].join(','),
    })
    const newProducts = res.data.data
    let nextProductMap = { ...productMap }
    newProducts.forEach((product) => {
      nextProductMap = {
        ...nextProductMap,
        [product.id]: product,
      }
    })

    mutate({
      ...res,
      data: {
        ...res.data,
        data: Object.values(nextProductMap).filter(product => product !== undefined),
      },
    }, {
      revalidate: false,
    })
    return res.data.data
  })

  const getProductsInfo = useCallbackEvent(async (extraProductIds?: CartItem['cartItemId'][]) => {
    const { productIdsSet, subshipProductIdsSet } = parseCartItemProductIdsSet(cartItems, extraProductIds)
    const { data } = await getProducts({
      project_code: projectCode,
      country_code: countryCode,
      product_ids: [...productIdsSet].join(','),
      subship_product_ids: [...subshipProductIdsSet].join(','),
    })
    return data.data
  })

  return {
    productMap,
    revalidateProductsInfo,
    getProductsInfo,
    isProductsLoading,
    isProductsValidating,
    isProductIdsStale: productIds !== debouncedProductIds,
    getProducts,
  }
}
